// AC GUIDE 7: VRV OR SPLIT ACs FOR A HOME? (26 Oct 2026) /blog/vrv-vs-split-ac
//
// Written to GUIDES_PLAYBOOK.md section 3, in the new layout from the
// structure mockup (25 Oct 2026): the answer, the animation, the simple
// table, one rupee example, pros and cons, a tip from the showroom, short
// parts, where to see it, questions, one tool.
//
// THE ANSWER IS SAHEB'S (25 Oct 2026): VRV when there is no outdoor space and
// you want a clean balcony look; it is "high capex, low opex": its ISEER is
// always higher than a 5 Star split, and a VRV system lasts longer than split
// ACs; it needs planning ahead and costs more up front; on average about
// ₹65,000 to ₹70,000 per HP; VRV Home has limited indoor unit options, VRV S
// has more. Daikin India's VRV Home page gives the product facts.
//
// Same rules as the other guides. Split prices come live from the catalogue
// via pickGuideModels(). No imports. **double stars** mean bold.

export const VRV_SLUG = 'blog/vrv-vs-split-ac';
export const VRV_PUBLISHED = '2026-10-26';
export const VRV_UPDATED = '2026-10-26';

export const VRV_SEO = {
  title: 'VRV vs Split AC for Home: Which Is Better? | Citi Cool',
  meta: 'VRV or split AC for your home? VRV when you have no space for outdoor units and want a clean balcony. It costs more up front and less to run. See the cost.',
  eyebrow: 'AC Guide · Buying',
  h1: 'VRV or split AC: which is better for your home?',
  crumb: 'VRV vs split AC',
  readMins: 4,
};

// Saheb, 25 Oct 2026: "an average would cost 65-70K per HP"; 26 Oct: that is the
// estimated cost with execution, so the split ACs it is compared with carry
// their installation too (bill() for EXAMPLE in data/guideInstall.js).
export const PER_HP = [65000, 70000];
// Daikin VRV Home outdoor units: 4, 5 and 6 HP = 11.2, 14.0 and 16 kW.
export const VRV_HOME_HP = [{ hp: 4, kw: 11.2 }, { hp: 5, kw: 14.0 }, { hp: 6, kw: 16 }];
export const EXAMPLE_HP = 5;
export const SPLITS_IN_EXAMPLE = 3;   // three 1.5 ton 5 Star splits, for the rupee example

// ─── sources ──────────────────────────────────────────────────────────────

export const VRV_SOURCES = [
  { key: 'saheb', label: 'Our showroom advice on VRV for homes: when it suits, cost per HP, running cost, life and planning (October 2026)', by: 'Citi Cool', href: null },
  { key: 'daikin-vrvhome', label: 'VRV Home: up to 8 indoor units on one outdoor unit, single-phase power, 4 to 6 HP, 40 m pipe, heat pump models', by: 'Daikin India', href: 'https://daikinindia.com/products-services/vrv-air-conditioner/vrv-home/vrv-home' },
  { key: 'daikin-split', label: 'Split AC product pages: pipe length limits (1.5 ton: 20 to 25 m, by model)', by: 'Daikin India', href: 'https://daikinindia.com/product-category/splitac' },
  { key: 'bee-iseer', label: 'Indian Seasonal Energy Efficiency Ratio', by: 'Vikaspedia, Government of India', href: 'https://en.vikaspedia.in/viewcontent/energy/policy-support/energy-efficiency/indian-seasonal-energy-efficiency-ratio' },
  { key: 'install', label: 'How much does AC installation cost in Delhi? Our rate card, with GST', by: 'Citi Cool', href: '/blog/ac-installation-cost' },
  { key: 'prices', label: 'Daikin 1.5 ton AC prices and stock today', by: 'citicool.in', href: '/daikin-1-5-ton-ac-price' },
  { key: 'units', label: 'Our indoor unit corner: all 15 Daikin VRV indoor units, with their look and finish', by: 'Citi Cool', href: '/architects-interior-designers#units' },
  { key: 'centre', label: 'Our VRV experience centre, South Extension Part-2, by appointment', by: 'Citi Cool', href: '/daikin-vrv-systems' },
];

// ─── arithmetic ───────────────────────────────────────────────────────────

const KW_PER_TR = 3.517;
export const tonOf = (kw) => Math.round((kw / KW_PER_TR) * 2) / 2;   // to the nearest half ton
export const lakh = (n) => `₹${(Math.round(n / 10000) / 10).toLocaleString('en-IN')} lakh`;
const inr = (n) => `₹${Math.round(n).toLocaleString('en-IN')}`;

// The tool's sentence: one VRV Home size -> its average cost.
export function costText(hp) {
  const u = VRV_HOME_HP.find((x) => x.hp === hp) || VRV_HOME_HP[1];
  return `A ${u.hp} HP VRV (about ${tonOf(u.kw)} ton) costs about **${lakh(u.hp * PER_HP[0])} to ${lakh(u.hp * PER_HP[1])}**, with execution. This is an estimate: your home decides the final price.`;
}

// ─── the page ─────────────────────────────────────────────────────────────

// five: the 1.5 ton 5 Star split from pickGuideModels() (live price);
// installEach: one split's installation with GST, bill(withGst(RATES, GST_RATE),
// EXAMPLE).total from data/guideInstall.js (3 m of each pipe run and a stand).
export function vrvContent(five, installEach = 0) {
  const ex = VRV_HOME_HP.find((x) => x.hp === EXAMPLE_HP);
  const splits = five && installEach ? (five.price + installEach) * SPLITS_IN_EXAMPLE : null;

  return {
    answer: {
      text: '**Choose VRV when you have no space for outdoor units and want a clean balcony. Choose split ACs when you want the lowest cost up front.**',
      src: ['saheb'],
    },
    because: [
      { text: 'One VRV outdoor unit can run up to 8 indoor units.', src: ['daikin-vrvhome'] },
      { text: 'VRV costs more to buy, but less to run. It also lasts longer.', src: ['saheb'] },
      { text: 'It needs planning before your home is built or redone.', src: ['saheb'] },
    ],
    watch: {
      title: 'Watch: the same home with split ACs and with VRV',
      captions: [
        { at: 0, text: 'A home with four rooms to cool.' },
        { at: 0.18, text: 'With split ACs, each room needs its own outdoor unit on the wall or balcony.' },
        { at: 0.5, text: 'With VRV, one outdoor unit runs every room. It can sit on the terrace.' },
        { at: 0.78, text: 'The balcony stays clear. The indoor units can hide in the ceiling.' },
      ],
      note: 'A simple drawing. Daikin\'s VRV Home runs up to 8 indoor units from one outdoor unit.',
      noteSrc: ['daikin-vrvhome'],
    },
    table: {
      title: 'VRV or split AC: which suits your home?',
      head: ['Your home', 'Choose'],
      rows: [
        ['No space for outdoor units', 'VRV'],
        ['You want a clean balcony look', 'VRV'],
        ['Long daily use, lower bills over the years', 'VRV'],
        ['You want the lowest price today', 'Split ACs'],
        ['No time to plan pipes and ceilings ahead', 'Split ACs'],
      ],
      note: 'The rule: **VRV costs more to buy and less to run.**',
      src: ['saheb'],
    },
    example: splits ? {
      title: 'In rupees: one real example',
      lines: [
        { label: `VRV, ${ex.hp} HP (about ${tonOf(ex.kw)} ton), with execution, estimated`, value: `${lakh(ex.hp * PER_HP[0])} to ${lakh(ex.hp * PER_HP[1])}` },
        { label: `${SPLITS_IN_EXAMPLE} split ACs, 1.5 ton 5 Star (${five.code}), with installation`, value: `about ${lakh(splits)}` },
      ],
      text: `Both include the work. VRV is an estimate: about ${inr(PER_HP[0])} to ${inr(PER_HP[1])} per HP with execution, on average. Each split AC is our price today plus about ${inr(Math.round(installEach / 100) * 100)} to install. That covers 3 m of pipe and a stand. VRV costs more up front and less to run.`,
      src: ['saheb', 'prices', 'install'],
    } : null,
    sides: [
      {
        key: 'vrv',
        title: 'VRV: pros and cons',
        good: [
          { text: 'One outdoor unit for the whole home. The balcony stays clear.', src: ['daikin-vrvhome', 'saheb'] },
          { text: 'It uses less electricity than even a [5 Star split AC](/blog/3-star-vs-5-star-ac).', src: ['saheb'] },
          { text: 'A VRV system lasts longer than split ACs.', src: ['saheb'] },
          { text: 'Heat pump models also heat your home in winter.', src: ['daikin-vrvhome'] },
        ],
        bad: [
          { text: 'It costs more up front.', src: ['saheb'] },
          { text: 'Pipes and ceilings must be planned before work starts.', src: ['saheb'] },
          { text: 'VRV Home offers fewer indoor unit styles. VRV S has more.', src: ['saheb'] },
        ],
      },
      {
        key: 'split',
        title: 'Split ACs: pros and cons',
        good: [
          { text: 'They cost less to buy.', src: ['prices'] },
          { text: 'Each one goes in on its own, any time. No pre-planning.', src: ['saheb'] },
          { text: 'You can buy one room at a time.', src: ['prices'] },
        ],
        bad: [
          { text: 'Every room needs its own outdoor unit outside.', src: ['daikin-vrvhome'] },
          { text: 'Each outdoor unit must sit close to its room.', src: ['daikin-split'] },
          { text: 'They use more electricity than VRV.', src: ['saheb'] },
        ],
      },
    ],
    tip: {
      text: 'VRV is high capex, low opex.',
      meaning: 'It costs more to buy, and less to run.',
      by: 'Saheb Sareen',
      src: ['saheb'],
    },
    parts: [
      {
        id: 'what',
        q: 'What is a VRV AC system?',
        bullets: [
          { text: 'It is one outdoor unit joined to many indoor units.', src: ['daikin-vrvhome'] },
          { text: 'Daikin\'s VRV Home runs up to 8 indoor units, on normal home power.', src: ['daikin-vrvhome'] },
          { text: 'Inside, each room gets its own unit: on the wall, or hidden in the ceiling.', src: ['daikin-vrvhome'] },
        ],
      },
      {
        id: 'cost',
        q: 'How much does a VRV system cost for a home?',
        bullets: [
          { text: 'On average, about ₹65,000 to ₹70,000 per HP, with execution.', src: ['saheb'] },
          { text: 'This is an estimate. Your home decides the final price.', src: ['saheb'] },
          { text: `Daikin sizes VRV Home in HP: 4, 5 or 6 HP. That is about ${VRV_HOME_HP.map((u) => tonOf(u.kw)).join(', ').replace(/, ([^,]*)$/, ' or $1')} ton.`, src: ['daikin-vrvhome'] },
          { text: 'Split ACs cost less, even with [installation](/blog/ac-installation-cost). Our tool at the bottom estimates a VRV price.', src: ['prices', 'install'] },
        ],
      },
      {
        id: 'bill',
        q: 'Does VRV use less electricity than split ACs?',
        bullets: [
          { text: 'Yes. ISEER is like a car\'s mileage: the cooling you get for each unit of electricity.', src: ['bee-iseer'] },
          { text: 'A VRV system\'s ISEER is always higher than a 5 Star split AC\'s.', src: ['saheb'] },
          { text: 'How much you save depends on how long you run it.', src: ['saheb'] },
        ],
      },
      {
        id: 'plan',
        q: 'What do you need to plan before VRV?',
        bullets: [
          { text: 'Plan it before your home is built or redone.', src: ['saheb'] },
          { text: 'Decide where the pipes run and which rooms get a false ceiling.', src: ['saheb'] },
          { text: 'VRV Home pipes can run up to 40 m. So the outdoor unit can go on the terrace.', src: ['daikin-vrvhome'] },
        ],
      },
      {
        id: 'which-vrv',
        q: 'VRV Home or VRV S: which should you choose?',
        bullets: [
          { text: 'VRV Home has fewer indoor unit styles to choose from.', src: ['saheb'] },
          { text: 'VRV S has more, if you want a certain look in each room.', src: ['saheb'] },
          { text: 'See every indoor unit in our [indoor unit corner](/architects-interior-designers#units).', src: ['units'] },
        ],
      },
    ],
    fits: [
      { label: 'See it running', title: 'Visit our VRV experience centre', text: 'South Extension Part-2, by appointment.', href: '/daikin-vrv-systems', src: ['centre'] },
      { label: 'Pick the look', title: 'All 15 VRV indoor units', text: 'How each one looks in a room.', href: '/architects-interior-designers#units', src: ['units'] },
    ],
    faq: [
      {
        q: 'Is VRV better than split AC for a home?',
        a: 'Yes, if you have no outdoor space or want a clean balcony. For the lowest price today, split ACs.',
        src: ['saheb'],
      },
      {
        q: 'How many indoor units can one VRV outdoor unit run?',
        a: 'Up to 8 on Daikin\'s VRV Home.',
        src: ['daikin-vrvhome'],
      },
      {
        q: 'Does VRV need three-phase power?',
        a: 'Not VRV Home. Daikin builds it for normal single-phase home power.',
        src: ['daikin-vrvhome'],
      },
      {
        q: 'Can VRV heat a home in winter?',
        a: 'Yes, the heat pump models heat as well as cool. For one room, see our [hot and cold AC guide](/blog/hot-and-cold-ac).',
        src: ['daikin-vrvhome'],
      },
      {
        q: 'Does a VRV system last longer than split ACs?',
        a: 'Yes. A VRV system lasts longer than split ACs.',
        src: ['saheb'],
      },
      {
        q: 'Can I see VRV before I buy?',
        a: 'Yes. Visit our VRV experience centre in South Extension Part-2. Visits are by appointment.',
        src: ['centre'],
      },
    ],
    tool: {
      title: 'Estimate the cost of a VRV system',
      examples: VRV_HOME_HP.map((u) => costText(u.hp)),
    },
  };
}
